import React from 'react';
import { Box, Divider, Text } from '@nimbus-ds/components';
import { PaymentProviderComparisonResult } from 'commons/types';
import { formatCurrency, useTranslationWithPrefix } from 'commons/utils';
import { IStoreInfo } from 'redux/slices/types';

interface CalculatedResultsDetailsProps {
  storeInfo: IStoreInfo;
  results: PaymentProviderComparisonResult;
  isPagoNube: boolean;
  savingsAmount: number;
  installmentsQty: number;
}

const CalculatedResultsDetails: React.FC<CalculatedResultsDetailsProps> = ({
  storeInfo,
  results,
  isPagoNube,
  savingsAmount,
  installmentsQty,
}) => {
  const { t } = useTranslationWithPrefix('landing.feeCalculatorModal.details');

  const {
    totalAmount,
    currency,
    releaseDays,
    rateAmount,
    ratePercentageValue,
    transactionCostAmount,
    transactionCostPercentageValue,
    fixedFeeValue,
    taxPercentageValue,
    taxAmount,
  } = results;

  const totalFeeAmount = rateAmount + fixedFeeValue;

  return (
    <>
      <Divider />
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Text fontSize="caption" color="currentColor" fontWeight="bold">
          {t('amount')}
        </Text>
        <Text fontSize="caption" color="currentColor" fontWeight="bold">
          {formatCurrency(
            {
              value: totalAmount,
              currency,
            },
            storeInfo,
          )}
        </Text>
      </Box>

      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Text fontSize="caption" color="neutral-textLow">
          {t('releaseDays')}
        </Text>
        <Text fontSize="caption" color="currentColor" fontWeight="bold">
          {releaseDays === 0
            ? t('days', { count: 0 })
            : `${releaseDays} ${t('days', { count: releaseDays })}`}
        </Text>
      </Box>

      {installmentsQty > 1 && (
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Text fontSize="caption" color="neutral-textLow">
            {t('installmentsLabel')}
          </Text>
          <Text fontSize="caption" color="currentColor">
            {installmentsQty}
            {t('installmentsSuffix')}
          </Text>
        </Box>
      )}

      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Text fontSize="caption" color="neutral-textLow">
          {t('rateTitle', { ratePercentageValue })}
          {formatCurrency(
            {
              value: fixedFeeValue,
              currency,
            },
            storeInfo,
          )}
        </Text>
        <Text fontSize="caption" color="currentColor">
          -
          {formatCurrency(
            {
              value: totalFeeAmount,
              currency,
            },
            storeInfo,
          )}
        </Text>
      </Box>

      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Text fontSize="caption" color="neutral-textLow">
          {t('ivaLabel', { taxPercentageValue })}
        </Text>
        <Text fontSize="caption" color="currentColor">
          -
          {formatCurrency(
            {
              value: taxAmount,
              currency,
            },
            storeInfo,
          )}
        </Text>
      </Box>

      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Text fontSize="caption" color="neutral-textLow">
          {t('transactionCostTitle', {
            transactionCostPercentageValue,
          })}
        </Text>
        <Text
          fontSize="caption"
          color={isPagoNube ? 'success-interactive' : 'currentColor'}
          fontWeight={isPagoNube ? 'bold' : 'regular'}
        >
          {isPagoNube ? (
            <Box display="flex" flexDirection="column" alignItems="flex-end">
              {t('pagonubeTransactionCost')}
              <Text
                fontSize="caption"
                color="success-interactive"
                fontWeight="bold"
              >
                {t('youSave')}
                {formatCurrency(
                  {
                    value: savingsAmount,
                    currency,
                  },
                  storeInfo,
                )}
              </Text>
            </Box>
          ) : (
            <Text
              fontSize="caption"
              color="danger-interactive"
              fontWeight="bold"
            >
              -
              {formatCurrency(
                {
                  value: transactionCostAmount,
                  currency,
                },
                storeInfo,
              )}
            </Text>
          )}
        </Text>
      </Box>
    </>
  );
};

export default CalculatedResultsDetails;
